import z, { ZodSchema } from "zod";

const passwordSchema = z
  .string()
  .min(8, { message: "errors.password_too_short" })
  .regex(/^[\S]+.*[\S]+$/, { message: "errors.password_no_spaces" })
  .regex(/[A-Z]/, { message: "errors.password_uppercase" })
  .regex(/[a-z]/, { message: "errors.password_lowercase" })
  .regex(/[0-9]/, { message: "errors.password_digit" })
  .regex(/[^A-Za-z0-9]/, { message: "errors.password_symbol" });

export const usernameSchema = z
  .string()
  .min(3, { message: "errors.username_too_short" })
  .regex(/^[\S]+$/, { message: "errors.username_no_spaces" });

export interface SignUpFormData {
  username: string;
  email: string;
  password: string;
}

export interface SignUpResponse {
  confirmed: boolean;
}

export type LoginFormData =
  | { email: string; password: string; username?: undefined }
  | { username: string; password: string; email?: undefined };

export interface PasswordRecoveryConfirmationData {
  username: string;
  newPassword: string;
  code: string;
}

export interface PasswordRecoveryData {
  email: string;
}

export interface SignUpConfirmationData {
  email: string;
  code: string;
}

export interface PasswordRecoveryResponse {
  confirmed: boolean;
}

export const emailSchema = {
  email: z.string().email({ message: "errors.invalid_email" }),
};

export const passwordRecoverySchema: ZodSchema<PasswordRecoveryData> =
  z.object(emailSchema);

export const loginSchema: ZodSchema<LoginFormData> = z.union([
  z.object({
    email: z.string().email({ message: "errors.invalid_email" }),
    password: z.string().min(1, { message: "errors.invalid_login" }),
    username: z.undefined(),
  }),
  z.object({
    username: z.string().min(1, { message: "errors.username_too_short" }),
    password: z.string().min(1, { message: "errors.invalid_login" }),
    email: z.undefined(),
  }),
]);

export const signUpSchema: ZodSchema<SignUpFormData> = z.object({
  username: usernameSchema,
  ...emailSchema,
  password: passwordSchema,
});

export const passwordRecoveryConfirmationSchema = z.object({
  username: usernameSchema,
  newPassword: passwordSchema,
  newPasswordConfirm: z.string(),
  code: z.string().min(1),
});

export const newPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    newPasswordConfirm: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.newPassword !== data.newPasswordConfirm) {
      ctx.addIssue({
        code: "custom",
        path: ["newPasswordConfirm"],
        message: "errors.passwords_do_not_match",
      });
    }
  });

export const signUpConfirmationSchema: ZodSchema<
  Omit<SignUpConfirmationData, "email">
> = z.object({
  code: z.string().length(6),
  username: usernameSchema,
});
